/**
 * デジタルツール認証（SSO）
 * PDF仕様「デジタルツール経由でのアプリケーションログインフロー_20231201」に準拠
 */

import { apiCall, setDigitalToolsAuth } from './apiHelper';
import { shouldUseSSOAuth } from './env';

const STR_TOKEN = 'token';
const AUTH_TOKEN_KEY = 'digital_tools_auth_token';
const USER_INFO_KEY = 'digital_tools_user_info';
const APP_CONNECTION_TOKEN_KEY = 'app_connection_token';
const APP_CONNECTION_TOKEN_EXPIRY_KEY = 'app_connection_token_expiry';

class DigitalToolsAuth {
  constructor() {
    this.authToken = null;
    this.userInfo = null;
    this.appConnectionToken = null;
    this.authState = 'checking';
    this.callbacks = [];
    this.refreshInFlight = false;

    this.loadAuthFromStorage();
    this.loadAppConnectionToken();
    setDigitalToolsAuth(this);
  }

  onAuthStateChanged(callback) {
    this.callbacks.push(callback);
    callback(this.authState, this.userInfo || undefined);
    return () => {
      const index = this.callbacks.indexOf(callback);
      if (index > -1) {
        this.callbacks.splice(index, 1);
      }
    };
  }

  setAuthState(state, userInfo) {
    this.authState = state;
    if (userInfo) {
      this.userInfo = userInfo;
    }
    this.callbacks.forEach((callback) => {
      callback(state, this.userInfo || undefined);
    });
  }

  loadAuthFromStorage() {
    try {
      const tokenStr = localStorage.getItem(AUTH_TOKEN_KEY);
      const userInfoStr = localStorage.getItem(USER_INFO_KEY);

      if (tokenStr) {
        const token = JSON.parse(tokenStr);
        if (token.expiresAt > Date.now()) {
          this.authToken = token;
          if (userInfoStr) {
            this.userInfo = JSON.parse(userInfoStr);
          }
          // authenticated にはせず、checkAuth で usercheck するまで checking のまま
          return;
        }
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(USER_INFO_KEY);
      }
    } catch (error) {
      console.error('認証情報の読み込みエラー:', error);
    }

    this.setAuthState('unauthenticated');
  }

  saveAuthToStorage(token, userInfo) {
    try {
      localStorage.setItem(AUTH_TOKEN_KEY, JSON.stringify(token));
      if (userInfo) {
        localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
      }
    } catch (error) {
      console.error('認証情報の保存エラー:', error);
    }
  }

  clearAuthFromStorage() {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(USER_INFO_KEY);
    localStorage.removeItem(APP_CONNECTION_TOKEN_KEY);
    localStorage.removeItem(APP_CONNECTION_TOKEN_EXPIRY_KEY);
    this.authToken = null;
    this.userInfo = null;
    this.appConnectionToken = null;
  }

  loadAppConnectionToken() {
    try {
      const token = localStorage.getItem(APP_CONNECTION_TOKEN_KEY);
      const expiryStr = localStorage.getItem(APP_CONNECTION_TOKEN_EXPIRY_KEY);

      if (token && expiryStr) {
        const expiry = parseInt(expiryStr, 10);
        if (Date.now() < expiry - 5 * 60 * 1000) {
          let parsedToken = token;
          if (token.trim().startsWith('{')) {
            try {
              const parsed = JSON.parse(token);
              parsedToken = parsed.access_token || parsed.token || token;
            } catch (e) {
              parsedToken = token;
            }
          }
          this.appConnectionToken = parsedToken;
          return;
        }
        localStorage.removeItem(APP_CONNECTION_TOKEN_KEY);
        localStorage.removeItem(APP_CONNECTION_TOKEN_EXPIRY_KEY);
      }
    } catch (error) {
      console.error('アプリ接続認証トークンの読み込みエラー:', error);
    }
  }

  saveAppConnectionToken(token, expiresIn = 3600) {
    try {
      const expiry = Date.now() + expiresIn * 1000;
      localStorage.setItem(APP_CONNECTION_TOKEN_KEY, token);
      localStorage.setItem(APP_CONNECTION_TOKEN_EXPIRY_KEY, expiry.toString());
      this.appConnectionToken = token;
    } catch (error) {
      console.error('アプリ接続認証トークンの保存エラー:', error);
    }
  }

  async getAppConnectionToken() {
    if (this.appConnectionToken) {
      const expiryStr = localStorage.getItem(APP_CONNECTION_TOKEN_EXPIRY_KEY);
      if (expiryStr) {
        const expiry = parseInt(expiryStr, 10);
        if (Date.now() < expiry - 5 * 60 * 1000) {
          if (
            typeof this.appConnectionToken === 'string' &&
            this.appConnectionToken.trim().startsWith('{')
          ) {
            try {
              const parsed = JSON.parse(this.appConnectionToken);
              const parsedToken = parsed.access_token || parsed.token;
              if (parsedToken) {
                this.saveAppConnectionToken(
                  parsedToken,
                  Math.floor((expiry - Date.now()) / 1000)
                );
                return parsedToken;
              }
            } catch (e) {
              // keep as-is
            }
          }
          return this.appConnectionToken;
        }
      }
    }

    const clientId = process.env.REACT_APP_CLIENT_ID;
    const digitalToolsUrl = process.env.REACT_APP_DIGITAL_TOOLS_URL;

    if (!clientId || !digitalToolsUrl) {
      throw new Error(
        '認証情報が設定されていません（REACT_APP_CLIENT_ID, REACT_APP_DIGITAL_TOOLS_URL）'
      );
    }

    const proxyUrl = '/api/auth/digital-tools/token';
    const response = await apiCall(proxyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: clientId,
        digital_tools_url: digitalToolsUrl,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `アプリ接続認証トークン取得エラー: ${response.status} ${response.statusText} - ${errorText}`
      );
    }

    const responseData = await response.json();
    const tokenData = responseData.data || responseData;
    const accessToken = tokenData.access_token || tokenData.token;
    const expiresIn = tokenData.expires_in || 3600;

    if (!accessToken || typeof accessToken !== 'string') {
      throw new Error('アプリ接続認証トークンがレスポンスに含まれていません');
    }

    const trimmedToken = accessToken.trim();
    this.saveAppConnectionToken(trimmedToken, expiresIn);
    return trimmedToken;
  }

  getTokenFromUrl() {
    const urlObj = new URL(window.location.href);
    return urlObj.searchParams.get(STR_TOKEN);
  }

  removeTokenFromUrl() {
    const urlObj = new URL(window.location.href);
    urlObj.searchParams.delete(STR_TOKEN);
    window.history.replaceState(null, '', urlObj.toString());
  }

  async checkAuth() {
    if (!shouldUseSSOAuth()) {
      this.setAuthState('authenticated', {
        employee_no: 'dev-user',
        employee_name: '開発ユーザー',
      });
      return;
    }

    const token = this.getTokenFromUrl();

    if (token) {
      this.removeTokenFromUrl();
      await this.authenticateWithToken(token);
    } else if (this.authToken && this.authToken.expiresAt > Date.now()) {
      // localStorage だけでは信用せず、デジタルツール usercheck で再検証する
      await this.authenticateWithToken(this.authToken.token);
    } else {
      this.markUnauthenticatedIfSessionExpired();
    }
  }

  async authenticateWithToken(token) {
    try {
      const digitalToolsUrl = process.env.REACT_APP_DIGITAL_TOOLS_URL;
      if (!digitalToolsUrl) {
        this.setAuthState('unauthenticated');
        return;
      }

      let appConnectionToken;
      try {
        appConnectionToken = await this.getAppConnectionToken();
      } catch (error) {
        throw new Error(
          `アプリ接続認証トークンの取得に失敗しました: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }

      const apiName = process.env.REACT_APP_APP_NAME || 'promo-calc-tool';
      const proxyUrl = '/api/auth/digital-tools/usercheck';
      const response = await apiCall(proxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          app_connection_token: appConnectionToken,
          digital_tools_url: digitalToolsUrl,
          api_name: apiName,
          auth_token: token,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `認証APIエラー: ${response.status} ${response.statusText} - ${errorText}`
        );
      }

      const proxyResponseData = await response.json();
      const responseData = proxyResponseData.data || proxyResponseData;

      let userInfo = {};
      if (responseData.dataList) {
        if (typeof responseData.dataList === 'string') {
          userInfo = JSON.parse(responseData.dataList);
        } else {
          userInfo = responseData.dataList;
        }
      }

      if (responseData.success !== true || responseData.status !== 'normal') {
        throw new Error(
          `認証失敗: ${responseData.message || responseData.statusCode || 'Unknown error'}`
        );
      }

      const authToken = {
        token,
        // サーバー ApiAuthGuard キャッシュ（15分）に合わせ、過長なローカル信頼を避ける
        expiresAt: Date.now() + 15 * 60 * 1000,
      };

      this.authToken = authToken;
      this.userInfo = userInfo;
      this.saveAuthToStorage(authToken, userInfo);
      this.setAuthState('authenticated', userInfo);
    } catch (error) {
      console.error('デジタルツール認証エラー:', error);
      this.setAuthState('unauthenticated');
      throw error;
    }
  }

  /**
   * メモリ／localStorage 上のセッション期限だけを見る（Cookie 再認証は行わない）。
   * 期限切れまたは未保持なら unauthenticated。
   */
  markUnauthenticatedIfSessionExpired() {
    try {
      if (this.authToken && this.authToken.expiresAt > Date.now() && this.userInfo) {
        this.setAuthState('authenticated', this.userInfo);
        return;
      }
      this.setAuthState('unauthenticated');
    } catch (error) {
      console.error('セッション期限チェックエラー:', error);
      this.setAuthState('unauthenticated');
    }
  }

  redirectToDigitalTools() {
    const frontendUrl =
      process.env.REACT_APP_DIGITAL_TOOLS_FRONTEND_URL ||
      process.env.REACT_APP_DIGITAL_TOOLS_URL;
    if (frontendUrl) {
      const redirectUrl = frontendUrl.endsWith('/')
        ? frontendUrl.slice(0, -1)
        : frontendUrl;
      window.location.href = redirectUrl;
    } else {
      console.error(
        'REACT_APP_DIGITAL_TOOLS_FRONTEND_URL または REACT_APP_DIGITAL_TOOLS_URL が未設定です'
      );
      alert(
        '認証エラー: デジタルツールのURLが設定されていません。管理者に連絡してください。'
      );
    }
  }

  async getAuthToken(forceRefresh = false) {
    if (!shouldUseSSOAuth()) {
      return null;
    }

    if (forceRefresh) {
      if (this.refreshInFlight) {
        return this.authToken?.token || null;
      }
      this.refreshInFlight = true;
      try {
        const existing = this.authToken?.token;
        if (existing) {
          try {
            await this.authenticateWithToken(existing);
            return this.authToken?.token || null;
          } catch {
            // usercheck 失敗 → 再ログインへ
          }
        }
        this.clearAuthFromStorage();
        this.setAuthState('unauthenticated');
        return null;
      } finally {
        this.refreshInFlight = false;
      }
    }

    if (!this.authToken || this.authToken.expiresAt <= Date.now()) {
      this.markUnauthenticatedIfSessionExpired();
    }

    return this.authToken?.token || null;
  }

  getUserInfo() {
    return this.userInfo;
  }

  getAuthState() {
    return this.authState;
  }

  isAuthenticated() {
    if (!shouldUseSSOAuth()) {
      return true;
    }

    return (
      this.authState === 'authenticated' &&
      this.authToken !== null &&
      this.authToken.expiresAt > Date.now()
    );
  }

  async logout() {
    this.clearAuthFromStorage();
    this.setAuthState('unauthenticated');
    this.redirectToDigitalTools();
  }
}

export const digitalToolsAuth = new DigitalToolsAuth();
